import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../api'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { Button } from '../../components/ui/Button'
import { safeAdminError } from '../safe-ui'
import type { IbApplication, IbSubadmin } from '../../ib/api'

type IbClientRow = {
  id: number
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  status?: string
  createdAt?: string
  accounts?: Array<Record<string, unknown>>
}

export function AdminIbPage() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [applications, setApplications] = useState<IbApplication[]>([])
  const [subadmins, setSubadmins] = useState<IbSubadmin[]>([])
  const [walletRequests, setWalletRequests] = useState<Array<Record<string, unknown>>>([])
  const [busyId, setBusyId] = useState<number | null>(null)

  const [approveId, setApproveId] = useState<number | null>(null)
  const [code, setCode] = useState('')
  const [profitPct, setProfitPct] = useState('0')
  const [lossPct, setLossPct] = useState('0')
  const [reserve, setReserve] = useState('0')
  const [depositCommPct, setDepositCommPct] = useState('5')
  const [holdDays, setHoldDays] = useState('14')

  const [creditId, setCreditId] = useState<number | null>(null)
  const [creditAmount, setCreditAmount] = useState('')
  const [creditNote, setCreditNote] = useState('')

  const [termsId, setTermsId] = useState<number | null>(null)
  const [termsPct, setTermsPct] = useState('5')
  const [termsHold, setTermsHold] = useState('14')

  const [clientsFor, setClientsFor] = useState<IbSubadmin | null>(null)
  const [ibClients, setIbClients] = useState<IbClientRow[]>([])
  const [clientsLoading, setClientsLoading] = useState(false)
  const [clientsError, setClientsError] = useState('')
  const viewingClientsId = useRef<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [apps, sas, wr] = await Promise.all([
        adminApi.ibApplications('PENDING'),
        adminApi.ibSubadmins(),
        adminApi.ibWalletRequests('PENDING'),
      ])
      setApplications((apps.applications || []) as IbApplication[])
      setSubadmins((sas.subadmins || []) as IbSubadmin[])
      setWalletRequests(wr.requests || [])
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load IB data'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function onApprove(e: FormEvent) {
    e.preventDefault()
    if (approveId == null) return
    setBusyId(approveId)
    setError('')
    setNotice('')
    try {
      await adminApi.ibApproveApplication(approveId, {
        code: code.trim() || undefined,
        profitSharePercent: Number(profitPct),
        lossSharePercent: Number(lossPct),
        minimumReserve: Number(reserve),
        depositCommissionPercent: Number(depositCommPct),
        commissionHoldDays: Number(holdDays),
      })
      setNotice(`Application #${approveId} approved.`)
      setApproveId(null)
      setCode('')
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Approve failed'))
    } finally {
      setBusyId(null)
    }
  }

  async function onReject(id: number) {
    setBusyId(id)
    setError('')
    try {
      await adminApi.ibRejectApplication(id, 'Rejected by admin')
      setNotice(`Application #${id} rejected.`)
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Reject failed'))
    } finally {
      setBusyId(null)
    }
  }

  async function onSetTerms(e: FormEvent) {
    e.preventDefault()
    if (termsId == null) return
    setBusyId(termsId)
    setError('')
    setNotice('')
    try {
      await adminApi.ibSetCommissionTerms(termsId, {
        depositCommissionPercent: Number(termsPct),
        commissionHoldDays: Number(termsHold),
      })
      setNotice(`Deposit commission terms updated for sub-admin #${termsId}.`)
      setTermsId(null)
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Commission terms update failed'))
    } finally {
      setBusyId(null)
    }
  }

  async function onCredit(e: FormEvent) {
    e.preventDefault()
    if (creditId == null) return
    setBusyId(creditId)
    setError('')
    try {
      await adminApi.ibManualCredit(creditId, Number(creditAmount), creditNote.trim())
      setNotice(`Manual credit posted to sub-admin #${creditId}.`)
      setCreditId(null)
      setCreditAmount('')
      setCreditNote('')
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Credit failed'))
    } finally {
      setBusyId(null)
    }
  }

  async function onWalletApprove(id: number) {
    setBusyId(id)
    setError('')
    try {
      await adminApi.ibApproveWalletRequest(id)
      setNotice(`Wallet request #${id} approved.`)
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Wallet approve failed'))
    } finally {
      setBusyId(null)
    }
  }

  const closeClientsPanel = useCallback(() => {
    viewingClientsId.current = null
    setClientsFor(null)
    setIbClients([])
    setClientsError('')
    setClientsLoading(false)
  }, [])

  useEffect(() => {
    if (clientsFor == null) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeClientsPanel()
      }
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [clientsFor, closeClientsPanel])

  async function onViewClients(s: IbSubadmin) {
    if (clientsFor?.id === s.id) {
      closeClientsPanel()
      return
    }
    setTermsId(null)
    setCreditId(null)
    viewingClientsId.current = s.id
    setClientsFor(s)
    setClientsLoading(true)
    setClientsError('')
    try {
      const data = await adminApi.ibSubadminClients(s.id)
      if (viewingClientsId.current !== s.id) return
      setIbClients(data.clients || [])
      setSubadmins((prev) =>
        prev.map((row) => (row.id === s.id ? { ...row, clientCount: data.clientCount } : row)),
      )
    } catch (err) {
      if (viewingClientsId.current !== s.id) return
      setClientsError(safeAdminError(err, 'Unable to load clients'))
      setIbClients([])
    } finally {
      if (viewingClientsId.current === s.id) setClientsLoading(false)
    }
  }

  async function onWalletReject(id: number) {
    setBusyId(id)
    setError('')
    try {
      await adminApi.ibRejectWalletRequest(id, 'Rejected by admin')
      setNotice(`Wallet request #${id} rejected.`)
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Wallet reject failed'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      <PageHeader eyebrow="Partners" title="IB / Sub-admins">
        <p className="cp-muted">
          Approve partners, set deposit commission % (14-day withdrawable hold by default), optional
          P/L share, wallet requests, and share <code>/ib/apply</code>.
        </p>
      </PageHeader>

      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}
      {error ? (
        <StateBlock tone="error" title="Error">
          {error}
        </StateBlock>
      ) : null}
      {notice ? (
        <StateBlock tone="info" title="Done">
          {notice}
        </StateBlock>
      ) : null}

      <section className={`cp-panel${loading ? ' is-refreshing' : ''}`} style={{ marginTop: '1rem' }}>
        <h2>Pending applications</h2>
        <ResponsiveTable
          rowKey={(a) => String(a.id)}
          columns={[
            { key: 'id', header: 'ID', render: (a) => a.id },
            { key: 'name', header: 'Name', render: (a) => a.displayName },
            { key: 'email', header: 'Email', render: (a) => a.email },
            { key: 'phone', header: 'Phone', render: (a) => a.phone || '—' },
            { key: 'notes', header: 'Notes', render: (a) => a.notes || '—' },
            {
              key: 'actions',
              header: 'Actions',
              render: (a) => (
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busyId === a.id}
                    onClick={() => {
                      setApproveId(a.id)
                      setCode('')
                    }}
                  >
                    Approve
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={busyId === a.id}
                    onClick={() => void onReject(a.id)}
                  >
                    Reject
                  </Button>
                </div>
              ),
            },
          ]}
          rows={applications}
          empty={loading ? 'Loading…' : 'No pending IB applications.'}
        />

        {approveId != null ? (
          <form className="cp-panel cp-auth__form" onSubmit={(e) => void onApprove(e)} style={{ marginTop: '1rem' }}>
            <h3>Approve application #{approveId}</h3>
            <label className="cp-field">
              <span className="cp-field__label">IB code (blank = auto)</span>
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={16} />
            </label>
            <label className="cp-field">
              <span className="cp-field__label">Deposit commission %</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={depositCommPct}
                onChange={(e) => setDepositCommPct(e.target.value)}
                required
              />
            </label>
            <label className="cp-field">
              <span className="cp-field__label">Commission hold days (withdrawable after)</span>
              <input
                type="number"
                min="0"
                max="365"
                step="1"
                value={holdDays}
                onChange={(e) => setHoldDays(e.target.value)}
                required
              />
            </label>
            <label className="cp-field">
              <span className="cp-field__label">Profit share % (optional, 0 = off)</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={profitPct}
                onChange={(e) => setProfitPct(e.target.value)}
                required
              />
            </label>
            <label className="cp-field">
              <span className="cp-field__label">Loss share % (optional, 0 = off)</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={lossPct}
                onChange={(e) => setLossPct(e.target.value)}
                required
              />
            </label>
            <label className="cp-field">
              <span className="cp-field__label">Minimum reserve (USD)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={reserve}
                onChange={(e) => setReserve(e.target.value)}
                required
              />
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button type="submit" disabled={busyId === approveId}>
                Confirm approve
              </Button>
              <Button type="button" variant="secondary" onClick={() => setApproveId(null)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : null}
      </section>

      <section className={`cp-panel${loading ? ' is-refreshing' : ''}`} style={{ marginTop: '1.75rem' }}>
        <h2>Active sub-admins</h2>
        <ResponsiveTable
          rowKey={(s) => String(s.id)}
          columns={[
            { key: 'code', header: 'Code', render: (s) => s.code },
            { key: 'name', header: 'Name', render: (s) => s.displayName },
            { key: 'email', header: 'Email', render: (s) => s.email },
            {
              key: 'depositComm',
              header: 'Deposit %',
              render: (s) =>
                `${Number(s.depositCommissionPercent || 0)}% / ${Number(s.commissionHoldDays ?? 14)}d`,
            },
            {
              key: 'totalComm',
              header: 'Total commission',
              render: (s) => `$${Number(s.totalCommission || 0).toFixed(2)}`,
            },
            {
              key: 'wdComm',
              header: 'Withdrawable commission',
              render: (s) => `$${Number(s.withdrawableCommission || 0).toFixed(2)}`,
            },
            {
              key: 'terms',
              header: 'P/L %',
              render: (s) => `${s.profitSharePercent}% / ${s.lossSharePercent}%`,
            },
            {
              key: 'balance',
              header: 'Balance',
              render: (s) => `$${Number(s.balance || 0).toFixed(2)}`,
            },
            {
              key: 'clients',
              header: 'Clients',
              render: (s) => Number(s.clientCount ?? 0),
            },
            {
              key: 'actions',
              header: 'Actions',
              render: (s) => (
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  <Button
                    type="button"
                    size="sm"
                    variant={clientsFor?.id === s.id ? 'primary' : 'secondary'}
                    disabled={clientsLoading && clientsFor?.id === s.id}
                    onClick={() => void onViewClients(s)}
                  >
                    {clientsFor?.id === s.id
                      ? 'Hide clients'
                      : `Clients (${Number(s.clientCount ?? 0)})`}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      closeClientsPanel()
                      setTermsId(s.id)
                      setTermsPct(String(s.depositCommissionPercent ?? 5))
                      setTermsHold(String(s.commissionHoldDays ?? 14))
                    }}
                  >
                    Deposit %
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      closeClientsPanel()
                      setCreditId(s.id)
                      setCreditAmount('')
                      setCreditNote('')
                    }}
                  >
                    Manual credit
                  </Button>
                </div>
              ),
            },
          ]}
          rows={subadmins}
          empty={loading ? 'Loading…' : 'No approved sub-admins yet.'}
        />

        {termsId != null ? (
          <form
            className="cp-panel cp-auth__form"
            onSubmit={(e) => void onSetTerms(e)}
            style={{ marginTop: '1rem' }}
          >
            <h3>Deposit commission · sub-admin #{termsId}</h3>
            <label className="cp-field">
              <span className="cp-field__label">Deposit commission %</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={termsPct}
                onChange={(e) => setTermsPct(e.target.value)}
                required
              />
            </label>
            <label className="cp-field">
              <span className="cp-field__label">Hold days (withdrawable after)</span>
              <input
                type="number"
                min="0"
                max="365"
                step="1"
                value={termsHold}
                onChange={(e) => setTermsHold(e.target.value)}
                required
              />
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button type="submit" disabled={busyId === termsId}>
                Save terms
              </Button>
              <Button type="button" variant="secondary" onClick={() => setTermsId(null)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : null}

        {creditId != null ? (
          <form className="cp-panel cp-auth__form" onSubmit={(e) => void onCredit(e)} style={{ marginTop: '1rem' }}>
            <h3>Manual credit · sub-admin #{creditId}</h3>
            <label className="cp-field">
              <span className="cp-field__label">Amount (USD)</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                required
              />
            </label>
            <label className="cp-field">
              <span className="cp-field__label">Note</span>
              <input value={creditNote} onChange={(e) => setCreditNote(e.target.value)} />
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button type="submit" disabled={busyId === creditId}>
                Post credit
              </Button>
              <Button type="button" variant="secondary" onClick={() => setCreditId(null)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : null}
      </section>

      <section className={`cp-panel${loading ? ' is-refreshing' : ''}`} style={{ marginTop: '1.75rem' }}>
        <h2>Pending wallet requests</h2>
        <ResponsiveTable
          rowKey={(r, i) => String(r.id ?? i)}
          columns={[
            { key: 'id', header: 'ID', render: (r) => String(r.id) },
            {
              key: 'sa',
              header: 'Sub-admin',
              render: (r) => String(r.subadmin_id || r.subadminId || '—'),
            },
            {
              key: 'type',
              header: 'Type',
              render: (r) => String(r.request_type || r.type || '—'),
            },
            {
              key: 'amount',
              header: 'Amount',
              render: (r) => `$${Number(r.amount || 0).toFixed(2)}`,
            },
            { key: 'note', header: 'Note', render: (r) => String(r.note || '—') },
            {
              key: 'actions',
              header: 'Actions',
              render: (r) => (
                <div style={{ display: 'flex', gap: '0.35rem' }}>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busyId === Number(r.id)}
                    onClick={() => void onWalletApprove(Number(r.id))}
                  >
                    Approve
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={busyId === Number(r.id)}
                    onClick={() => void onWalletReject(Number(r.id))}
                  >
                    Reject
                  </Button>
                </div>
              ),
            },
          ]}
          rows={walletRequests}
          empty={loading ? 'Loading…' : 'No pending IB wallet requests.'}
        />
      </section>

      {clientsFor != null ? (
        <div
          className="ap-dialog-backdrop"
          role="presentation"
          onClick={closeClientsPanel}
        >
          <div
            className="ap-dialog ap-dialog--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ib-clients-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="ib-clients-title">
              Clients of {clientsFor.code}
              {clientsFor.displayName ? ` · ${clientsFor.displayName}` : ''}
            </h2>
            <div className="ap-dialog__body">
              <p className="cp-muted" style={{ margin: '0 0 0.75rem' }}>
                {clientsLoading
                  ? 'Loading referred clients…'
                  : `${ibClients.length} client${ibClients.length === 1 ? '' : 's'} referred by this IB / sub-admin.`}
              </p>
              {clientsError ? (
                <StateBlock tone="error" title="Error">
                  {clientsError}
                </StateBlock>
              ) : null}
              <ResponsiveTable
                rowKey={(c) => String(c.id)}
                columns={[
                  { key: 'id', header: 'ID', render: (c) => c.id },
                  {
                    key: 'name',
                    header: 'Client',
                    render: (c) =>
                      `${c.firstName || ''} ${c.lastName || ''}`.trim() || `Client #${c.id}`,
                  },
                  {
                    key: 'email',
                    header: 'Email / Mobile',
                    render: (c) => (
                      <span>
                        <strong>{c.email || '—'}</strong>
                        <br />
                        <span className="cp-muted">{c.phone || '—'}</span>
                      </span>
                    ),
                  },
                  { key: 'status', header: 'Status', render: (c) => c.status || '—' },
                  {
                    key: 'accounts',
                    header: 'MT5 accounts',
                    render: (c) =>
                      (c.accounts || [])
                        .map((a) => `${a.mode || '?'} ${a.mt5Login || a.mt5_login || ''}`.trim())
                        .join(', ') || '—',
                  },
                  {
                    key: 'created',
                    header: 'Joined',
                    render: (c) => (c.createdAt ? String(c.createdAt).slice(0, 10) : '—'),
                  },
                  {
                    key: 'open',
                    header: '',
                    mobileLabel: 'Open',
                    render: (c) => (
                      <Link
                        className="pp-btn pp-btn--secondary pp-btn--sm"
                        to={`/admin/clients/${c.id}`}
                        onClick={closeClientsPanel}
                      >
                        Open
                      </Link>
                    ),
                  },
                ]}
                rows={ibClients}
                empty={
                  clientsLoading
                    ? 'Loading…'
                    : 'This IB / sub-admin has no referred clients yet.'
                }
              />
            </div>
            <div className="ap-dialog__actions">
              <Button type="button" variant="secondary" onClick={closeClientsPanel}>
                Close
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
